// ---------------------------------------------------------------------------
// Candidate plans for the search bot
// ---------------------------------------------------------------------------
//
// generateCandidates(view, rng, k, odds, profile) → up to k legal, distinct
// plans. The full space (every deploy split × move × brood spot × attack
// subset × target cell × drop zone × purchase) is far too big to score, so it
// is pruned to a handful of plans that differ where it matters:
//
//   - each plan is built by one STYLE: how readily it attacks (by its edge in
//     a corridor), whether it moves, broods, buys rockets, sends a plane
//   - deploys go where the corridor is weakest against what the opponent
//     shows there (their drones and broods on the public board)
//   - targets are cells the sampled opponent Grids most often hold a live
//     structure in (the odds map), a brood under construction in that
//     corridor first — one destroyed cell kills it
//   - a plane launches in the corridor the player knows least about, aimed at
//     that corridor's most uncertain cell; a rocket goes on the structure in
//     the corridor the opponent is strongest in
//   - two fixed styles always compete: HOLD (no attacks, no buys) and ALL-IN
//     (attack every corridor with an edge); the rest are drawn at random
//   - every plan goes through validateOrders; duplicates are dropped
//
// A profile (strategy) may bias the styles: see profiles.js.

import { rules } from '../config.js'
import {
    moveUnit, deployArmy, buildFort, canAttack, canPlaceDropZone, requiredDropZones, basesOf,
    calculateCombatStrength,
} from '../gameLogic.js'
import { eligibleBaseCenters, baseFootprint } from '../subplotBases.js'
import { plotSubIndices, plotOf, battlefieldColumn, isStructureDestroyed } from '../subplotGrid.js'
import { validateOrders } from '../validateOrders.js'
import { hexDistance } from '../hexGrid.js'
import { craterFootprint } from '../subplotDamage.js'
import { assetCells, openSlots } from './randomBot.js'
import { pick, shuffled } from './rng.js'
import { PROFILES } from './profiles.js'
import { legalThreats, stakeInReach, reachOf } from './threats.js'

const clone = (v) => JSON.parse(JSON.stringify(v))
const corridorCells = (col) => [0, 1, 2, 3, 4].flatMap(row => plotSubIndices(col, row))

// What the opponent shows in each corridor: drones (their airdrops fight
// once they mature at submit) and mature brood drones.
export function oppStrengths(view) {
    return view.opp.board.map(bf => calculateCombatStrength(bf) + (bf.newArmies || 0))
}
const myStrength = (bf) => calculateCombatStrength(bf) + (bf.newArmies || 0) + (bf.movedInArmies || 0)

function weightedPick(rng, items, weight) {
    const ws = items.map(weight)
    const total = ws.reduce((a, b) => a + b, 0)
    if (total <= 0) return pick(rng, items)
    let r = rng() * total
    for (let i = 0; i < items.length; i++) { r -= ws[i]; if (r <= 0) return items[i] }
    return items[items.length - 1]
}

// The target in `col`: an opponent brood under construction there first
// (one hit kills it, and a construction doesn't fight, so it is still there
// when the crater lands), then the likeliest structure cell. Never a mature
// brood in the attacking corridor: a crater lands only if this corridor's
// clash is won, and a mature brood fires in that clash and is gone before
// the crater lands — the shot would hit empty land. Brood cells hold no
// structure either, so they are never aimed at for one.
// What a strike aimed at `aim` with a winning margin `margin` is expected to
// hit — the crater the rules will dig (craterFootprint), shrunk by the
// opponent's visible rockets covering the aim (each cancels a shot): the
// samples' odds of a live structure on each cell it covers, plus a bonus when
// it covers every live cell of a hit structure (a kill), and for catching a
// brood (a construction dies of one cell; a mature one in the attacking
// corridor fires and is gone before the crater lands, so only a neighbour's
// counts).
// `spent` (optional): cells this plan's earlier corridors already crater this
// round (corridors resolve 1 → 7) — as good as destroyed by the time this
// crater lands. `fresh` (optional): what each intact, on-grid cell the crater
// digs is worth on its own — a crater on old craters, our own earlier ones or
// off the grid's edge spends its shots on nothing.
export function aimScore(view, col, odds, aim, margin, spent = null, fresh = 0) {
    const grid = view.opp.grid
    const covering = grid.rockets.filter(r => hexDistance(r, aim) <= rules().rocketCoverageSteps).length
    const m = Math.max(0, margin - covering)
    if (m <= 0) return 0
    const gone = (s) => grid.cells[s].destroyed || !!spent?.has(s)
    const fp = craterFootprint(aim, m)
    const hit = new Set(fp)
    let v = 0
    for (const s of fp) if (!gone(s)) v += (odds?.[s] ?? 0) + fresh
    for (const g of grid.groups) {
        if (g.dead) continue
        const live = g.cells.filter(s => !gone(s))
        if (live.length && live.every(s => hit.has(s))) v += 2
    }
    for (const b of grid.broods) {
        if (b.spent || !b.footprint.some(s => hit.has(s)) || b.footprint.some(s => spent?.has(s))) continue
        if (b.underConstruction) v += 1.5
        else if (b.col !== col) v += 0.3
    }
    return v
}

// What an intact cell is worth to a crater on its own (aimScore's `fresh`):
// enough to break near-ties toward untouched ground, too little to outweigh a
// real belief about a structure.
const FRESH_CELL = 0.1

// An aim's worth when the margin may come up short: the opponent deploys
// after we see their board, so the crater is often one size smaller than
// planned — the aim is scored as the mean of the planned crater and the one a
// size down (a margin-1 plan has no smaller crater: it lands or it doesn't).
function robustAimScore(view, col, odds, aim, margin, spent, fresh) {
    const v = aimScore(view, col, odds, aim, margin, spent, fresh)
    return margin > 1 ? (v + aimScore(view, col, odds, aim, margin - 1, spent, fresh)) / 2 : v
}

// With `spread`, a mature brood in a NEIGHBOURING corridor is fair game: the
// shot goes on this corridor's cell nearest it, and the crater's spread does
// the damage (the simulation then judges whether the brood is still there —
// it fires when its own corridor's clash resolves, corridors 1 → 7).
// With `margin` (the expected winning margin), the aim is chosen for the
// crater it digs (aimScore): the best cell, or — not greedy — one drawn in
// proportion to its score — scored against a margin that may come up short,
// with the cells `spent` by this plan's earlier corridors as good as
// destroyed, and untouched ground preferred (FRESH_CELL). The fresh term only
// breaks ties: with no belief anywhere the aim falls through to the rules
// below.
export function targetFor(view, col, odds, rng, greedy = true, spread = false, margin = null, spent = null) {
    const gone = (s) => view.opp.grid.cells[s].destroyed || !!spent?.has(s)
    if (margin != null) {
        const believed = corridorCells(col).some(s => robustAimScore(view, col, odds, s, margin, spent, 0) > 0)
        const scored = corridorCells(col).map(s => ({ s, v: robustAimScore(view, col, odds, s, margin, spent, FRESH_CELL) }))
        if (believed) {
            if (greedy) return scored.reduce((a, b) => (b.v > a.v ? b : a)).s
            return weightedPick(rng, scored, x => 0.02 + x.v * x.v).s
        }
    }
    const conBrood = view.opp.grid.broods.find(b => b.col === col && b.underConstruction && !b.spent)
    if (conBrood) return conBrood.centerS
    if (spread) {
        const next = view.opp.grid.broods.find(b => !b.spent && !b.underConstruction && Math.abs(b.col - col) === 1)
        if (next) {
            const d = (s) => Math.min(...next.footprint.map(f => hexDistance(s, f)))
            return corridorCells(col).filter(s => !gone(s))
                .reduce((a, b) => (d(b) < d(a) ? b : a), corridorCells(col)[0])
        }
    }
    const broodCells = new Set([...view.opp.grid.broods.flatMap(b => b.footprint), ...(view.opp.grid.pastBroodCells ?? [])])
    let cells = corridorCells(col).filter(s => !gone(s) && !broodCells.has(s))
    if (!cells.length) cells = corridorCells(col).filter(s => !gone(s))
    if (!cells.length) return corridorCells(col)[4]
    const score = (s) => odds?.[s] ?? 0
    if (greedy) return cells.reduce((a, b) => (score(b) > score(a) ? b : a))
    return weightedPick(rng, cells, s => 0.05 + score(s))
}

function buildPlan(view, rng, odds, style) {
    let board = clone(view.self.board)
    const hf = view.self.homefront
    const opp = oppStrengths(view)
    // Threat-aware (style.threat, threats.legalThreats): a corridor is short
    // by what the opponent can legally strike with there this round — nothing
    // where they can't attack (no rights, an airdrop just landed) — and holds
    // when it matches it (a tie lands no crater). Otherwise, the old instinct:
    // short of beating whatever they show there.
    const threat = style.threat ?? null
    const threatBase = style.threatBase ?? threat
    const lost = view.self.structuresLost ?? 0
    const deficit = threat
        ? (c) => Math.max(0, threat[c] - myStrength(board[c]))
        : (c) => Math.max(0, opp[c] + 1 - myStrength(board[c]))
    // What a corridor shields: our standing structures with a live cell in it,
    // and its broods. A threat to a corridor guarding nothing may be let go —
    // the deploys swung where they do more (a stake-blind style keeps the old
    // instinct as an option). Threat-aware, it is what a crater won there
    // could reach — its neighbours' broods and structures too.
    const stakeOf = (c) => {
        if (style.stakeBlind) return 1
        if (threat) return 0.2 + stakeInReach(hf, board, c, deficit(c), lost)
        let n = 0.2
        for (const [id, st] of Object.entries(hf?.structures ?? {})) {
            if (isStructureDestroyed(hf, id)) continue
            if (st.footprint.some(s => battlefieldColumn(s) === c && !hf.subplots[s].destroyed)) n += 1
        }
        return n + 1.5 * basesOf(board[c]).length
    }
    // What the corridors a brood's footprint lies in reach of are short, in
    // all (a safe brood is one this round's deploys can cover).
    const shortAround = (centerS, c) => {
        const fp = baseFootprint(centerS) ?? []
        let n = 0
        for (const k of [c - 1, c, c + 1]) {
            if (!board[k] || deficit(k) <= 0) continue
            const reach = reachOf(k, deficit(k))
            if (fp.some(s => reach.has(s))) n += deficit(k)
        }
        return n
    }

    // Guard moves: a veteran from a corridor that can spare it (still holds
    // what can strike there) into a neighbour short of guarding something.
    if (style.guard && threat) {
        const needy = board.map((_, c) => c).filter(c => deficit(c) > 0 && stakeOf(c) > 0.2)
            .sort((a, b) => stakeOf(b) * deficit(b) - stakeOf(a) * deficit(a))
        for (const n of needy) {
            for (const m of [n - 1, n + 1]) {
                const bf = board[m]
                if (!bf || !(bf.armyQty > 0) || bf.movedOut || deficit(n) <= 0) continue
                if (myStrength(bf) - 1 < threat[m]) continue
                const r = moveUnit(board, bf.id, m < n ? 'right' : 'left')
                if (r.success) board = r.boardArr
            }
        }
    }
    // Moves: one drone toward the weaker neighbour of the two, where it helps.
    if (style.moves) {
        for (const c of shuffled(rng, board.map((_, i) => i))) {
            if (rng() >= style.moves) continue
            const dir = deficit(c - 1 >= 0 ? c - 1 : c) >= deficit(c + 1 < board.length ? c + 1 : c) ? 'left' : 'right'
            const r = moveUnit(board, board[c].id, dir)
            if (r.success) board = r.boardArr
        }
    }
    // Broods. A safe brood (threat-aware) is built only where this round's
    // deploys can still cover every corridor a crater could reach it from —
    // the build spends two veterans, and a construction fights at 0.
    const broodCols = []
    let cover = rules().armiesPerRound
    for (const c of shuffled(rng, board.map((_, i) => i))) {
        const bf = board[c]
        const want = style.broodCols?.includes(c) ? 1 : style.brood
        if ((bf.armyQty || 0) < 2 || rng() >= want) continue
        const pending = new Set((bf.pendingBaseBuilds ?? []).flatMap(p => baseFootprint(p.centerS) ?? []))
        const spots = eligibleBaseCenters(hf, c).filter(s => !(baseFootprint(s) ?? []).some(x => pending.has(x)))
        if (!spots.length) continue
        // The row furthest from the band is the hardest to reach.
        const spot = style.broodDeep ? spots[spots.length - 1] : pick(rng, spots)
        const r = buildFort(board, bf.id, spot)
        if (!r.success) continue
        if (style.safeBrood && threat) {
            const before = board
            board = r.boardArr
            const short = shortAround(spot, c)
            if (short > cover) { board = before; continue }
            cover -= short
        } else board = r.boardArr
        broodCols.push(c)
    }
    // Deploys: where the corridor is weakest. Covering broods (threat-aware,
    // on unless a style turns it off): first into any corridor short of what
    // they can legally strike with there whose crater could reach one of our
    // broods under construction — its own corridor or a neighbour — the most
    // short first. That is the round a brood is laid and the round after,
    // while it is building: one cell kills it, and laying it spent two
    // veterans. Guarding (threat-aware), each then goes where a strike could
    // reach the most of ours, until nothing in reach is short.
    let owed = rules().armiesPerRound
    if (threat && style.coverBroods !== false) {
        const building = board.flatMap((bf, c) => basesOf(bf).filter(b => !b.mature).map(b => ({ c, fp: baseFootprint(b.centerS) ?? [] })))
        const exposes = (k) => deficit(k) > 0 && room(board[k]) > 0 &&
            building.some(b => Math.abs(b.c - k) <= 1 && b.fp.some(s => reachOf(k, deficit(k)).has(s)))
        for (let tries = 0; owed > 0 && building.length && tries < 10; tries++) {
            const open = board.map((_, k) => k).filter(exposes)
            if (!open.length) break
            const k = open.reduce((x, y) => (deficit(y) > deficit(x) ? y : x))
            const r = deployArmy(board, board[k].id, owed)
            if (!r.success) break
            board = r.boardArr; owed = r.armiesToDeploy
        }
    }
    if (style.guard && threat) {
        // First close whole gaps: of the corridors short of what they can
        // legally strike with there and guarding something, the set this
        // round's drones can cover completely, the most at stake. A drone that
        // leaves a corridor still short only shrinks the crater — two half-
        // covered corridors are both still open.
        // Each short corridor can be closed to what is already there (their
        // veterans and broods, threatBase) — which stops most strikes, worth
        // BASE_CLOSE of a full close — or to the most they could bring with
        // movers (threat), which stops all of them.
        const BASE_CLOSE = 0.6
        // (gapClose off: the earlier guard, straight to shrinking the worst.)
        const needy = style.gapClose === false ? [] : board.map((_, c) => c).filter(c => deficit(c) > 0 && stakeOf(c) > 0.2)
        const opts = needy.map(c => {
            const s = stakeOf(c), have = myStrength(board[c]), roomLeft = room(board[c])
            const o = [{ c, cost: 0, value: 0 }]
            const toBase = Math.max(0, threatBase[c] - have)
            if (toBase > 0 && toBase <= roomLeft && threatBase[c] < threat[c]) o.push({ c, cost: toBase, value: BASE_CLOSE * s })
            if (deficit(c) <= roomLeft) o.push({ c, cost: deficit(c), value: s })
            return o
        })
        let best = { picks: [], value: 0, cost: 0 }
        const walk = (i, picks, cost, value) => {
            if (cost > owed) return
            if (i === opts.length) {
                if (value > best.value || (value === best.value && cost < best.cost)) best = { picks, value, cost }
                return
            }
            for (const o of opts[i]) walk(i + 1, o.cost ? [...picks, o] : picks, cost + o.cost, value + o.value)
        }
        walk(0, [], 0, 0)
        for (const { c, cost } of best.picks) {
            for (let n = cost; n > 0 && owed > 0; n--) {
                const r = deployArmy(board, board[c].id, owed)
                if (!r.success) break
                board = r.boardArr; owed = r.armiesToDeploy
            }
        }
        // Then, with drones left and no gap they can close, shrink the worst.
        for (let tries = 0; owed > 0 && tries < 10; tries++) {
            const needy = board.map((_, c) => c).filter(c => deficit(c) > 0 && stakeOf(c) > 0.2 && room(board[c]) > 0)
            if (!needy.length) break
            const c = needy.reduce((a, b) => (stakeOf(b) * Math.min(deficit(b), owed) > stakeOf(a) * Math.min(deficit(a), owed) ? b : a))
            const r = deployArmy(board, board[c].id, owed)
            if (!r.success) break
            board = r.boardArr; owed = r.armiesToDeploy
        }
    }
    const order = style.deployCols ?? null
    for (let tries = 0; owed > 0 && tries < 40; tries++) {
        const c = order && tries < order.length ? order[tries]
            : weightedPick(rng, board.map((_, i) => i), i => 0.3 + deficit(i) * style.defend * stakeOf(i))
        const r = deployArmy(board, board[c].id, owed)
        if (r.success) { board = r.boardArr; owed = r.armiesToDeploy }
    }
    for (let c = 0; owed > 0 && c < board.length; c++) {
        let r
        while (owed > 0 && (r = deployArmy(board, board[c].id, owed)).success) { board = r.boardArr; owed = r.armiesToDeploy }
    }
    // Attacks and targets.
    const targetPlots = Array(board.length).fill(null)
    const targetSubplots = Array(board.length).fill(null)
    // Cells this plan's craters dig, corridor by corridor in the order they
    // land (1 → 7): a crater spills into the next corridor, so a later aim
    // treats them as already destroyed rather than striking the same ground.
    const spent = new Set()
    for (let c = 0; c < board.length; c++) {
        if (!canAttack(board[c]).valid) continue
        if (style.noAttackCols?.includes(c)) continue
        const edge = myStrength(board[c]) - opp[c]
        // A patient style strikes only where it clearly outnumbers them.
        if (style.minEdge != null && edge < style.minEdge && !style.forceAttackCols?.includes(c)) continue
        const p = style.forceAttackCols?.includes(c) ? 1 : edge > 0 ? style.aggression : edge === 0 ? style.aggression * 0.3 : style.aggression * 0.08
        if (rng() >= p) continue
        board[c] = { ...board[c], isAttacking: true }
        const margin = Math.max(1, myStrength(board[c]) - opp[c])
        const s = (style.targetAt && style.targetAt(c)) ?? targetFor(view, c, odds, rng, rng() < style.greedyTarget, !!style.broodSpread, margin, spent)
        for (const x of craterFootprint(s, margin)) spent.add(x)
        targetSubplots[c] = s
        targetPlots[c] = plotOf(s).row
    }
    // Drop zones owed, where the corridor is weakest.
    const dropZones = []
    const owedZones = Math.min(requiredDropZones(board), openSlots(board))
    for (let tries = 0; dropZones.length < owedZones && tries < 60; tries++) {
        const c = weightedPick(rng, board.map((_, i) => i), i => 0.3 + deficit(i))
        if (canPlaceDropZone(board, c, dropZones).valid) dropZones.push(c)
    }
    for (let c = 0; dropZones.length < owedZones && c < board.length; c++) {
        while (dropZones.length < owedZones && canPlaceDropZone(board, c, dropZones).valid) dropZones.push(c)
    }
    // Purchases.
    const { rocketCost, droneCost } = rules()
    let spendable = view.self.yield
    const rockets = [], drones = []
    const cells = assetCells(hf).filter(({ s, st }) => !(st.rocketCells || []).includes(s))
    const used = new Set()
    if (style.rockets && spendable >= rocketCost) {
        // Nearest a cell to protect (a rocket covers cells within its
        // coverage radius of its own), else in the corridor the opponent is
        // strongest in.
        const near = style.rocketNearCells
        const key = (s) => (near ? -Math.min(...near.map(n => hexDistance(s, n))) : opp[battlefieldColumn(s)])
        const byThreat = [...cells].sort((a, b) => key(b.s) - key(a.s) || a.s - b.s)
        for (const { s } of byThreat) {
            if (spendable < rocketCost || rockets.length >= style.rockets) break
            rockets.push(s); used.add(s); spendable -= rocketCost
        }
    }
    if (style.plane && spendable >= droneCost) {
        const unc = (s) => { const p = odds?.[s] ?? 0; return view.opp.grid.cells[s].known === 'unknown' ? p * (1 - p) + 0.01 : 0 }
        const colUnc = (c) => corridorCells(c).reduce((a, s) => a + unc(s), 0)
        const launch = [...cells].filter(({ s }) => !used.has(s))
            .sort((a, b) => colUnc(battlefieldColumn(b.s)) - colUnc(battlefieldColumn(a.s)))[0]
        if (launch) {
            const col = style.planeCol ?? battlefieldColumn(launch.s)
            const l = [...cells].find(({ s }) => !used.has(s) && battlefieldColumn(s) === col) ?? launch
            const lc = battlefieldColumn(l.s)
            const targetS = corridorCells(lc).reduce((a, b) => (unc(b) > unc(a) ? b : a))
            drones.push({ launchS: l.s, targetS })
        }
    }
    return { board, targetPlots, targetSubplots, dropZones, rockets, drones, style: style.name, broodCols }
}

const HOLD = { name: 'hold', aggression: 0, moves: 0, brood: 0, defend: 1, greedyTarget: 1, rockets: 0, plane: false }

// Plans a one-round search would never invent but must be able to weigh —
// always offered, the search judging each on the simulated result:
//   stack     both deploys into one corridor (the best one with room, or the
//             runner-up), that stack kept home, strikes only at a 2+ edge
//   brood     build a brood wherever two veterans stand (deepest row), keep
//             those corridors home: an airdrop every round from maturity
//   prepare   no pair anywhere: both deploys where a pair is nearest, so a
//             brood is possible next round
//   hunt      every visible brood of theirs, from its own corridor (a
//             construction) and both neighbours (rules/bots/profiles.js)
//   patient   half the random styles strike only at a 2+ edge
const room = (bf) => bf.maxArmyQty - ((bf.armyQty || 0) + (bf.newArmies || 0) + (bf.movedInArmies || 0))
function stackStyles(view) {
    const board = view.self.board
    const opp = oppStrengths(view)
    const cols = board.map((_, c) => c).filter(c => room(board[c]) >= 2 && !board[c].isAttacking)
        .sort((a, b) => (myStrength(board[b]) - opp[b]) - (myStrength(board[a]) - opp[a]) || room(board[b]) - room(board[a]))
    return cols.slice(0, 2).map(c => ({
        name: `stack-c${c + 1}`, aggression: 1, minEdge: 2, moves: 0, brood: 0, defend: 1, greedyTarget: 1,
        rockets: 0, plane: false, deployCols: [c, c], noAttackCols: [c],
    }))
}
function broodStyles(view) {
    const board = view.self.board
    const cost = rules().broodCost
    const ready = board.map((bf, c) => ((bf.armyQty || 0) >= cost && !bf.isAttacking ? c : null)).filter(c => c != null)
    const base = { aggression: 0.5, minEdge: 2, moves: 0, brood: 0, defend: 1, greedyTarget: 1, rockets: 0, plane: false }
    if (ready.length) {
        return [{ ...base, name: 'brood', broodCols: ready, broodDeep: true, noAttackCols: ready, deployCols: [ready[0], ready[0]] }]
    }
    const near = board.map((_, c) => c).filter(c => room(board[c]) >= 2)
        .sort((a, b) => (board[b].armyQty || 0) - (board[a].armyQty || 0) || room(board[b]) - room(board[a]))[0]
    return near == null ? [] : [{ ...base, name: 'prepare-brood', deployCols: [near, near], noAttackCols: [near] }]
}

const ALL_IN = { name: 'all-in', aggression: 1, moves: 0, brood: 0, defend: 0.5, greedyTarget: 1, rockets: 0, plane: false }
function randomStyle(rng, i) {
    return {
        name: `style-${i}`,
        aggression: pick(rng, [0.2, 0.5, 0.8, 1]),
        moves: pick(rng, [0, 0, 0.2, 0.4]),
        brood: pick(rng, [0, 0, 0.3, 0.7]),
        broodDeep: rng() < 0.5,
        defend: pick(rng, [0.5, 1, 2]),
        greedyTarget: pick(rng, [0.6, 1]),
        rockets: pick(rng, [0, 0, 1, 2]),
        plane: rng() < 0.3,
        // Aim next to a neighbouring corridor's mature brood when there is one.
        broodSpread: rng() < 0.3,
        // Patient: strike only with a clear edge.
        minEdge: rng() < 0.5 ? 2 : null,
        stakeBlind: rng() < 0.3,
    }
}

const signature = (o) => JSON.stringify([
    o.board.map(bf => [bf.armyQty, bf.newArmies, bf.movedInArmies, bf.isAttacking, (bf.pendingBaseBuilds ?? []).map(p => p.centerS)]),
    o.targetSubplots, o.dropZones, o.rockets, o.drones,
])

// Threat-aware plans (threats.js) — always offered when the search is:
//   guard       meet what they can legally strike with wherever a crater
//               could reach ours, most at stake first: a veteran moved in
//               from a neighbour that can spare it, then the deploys; strikes
//               only at a 2+ edge
//   guard-all   the same, striking wherever it has an edge
//   safe-brood  brood where two veterans stand, but only where this round's
//               deploys can cover every corridor a crater could reach the
//               construction from — then guard it
function guardStyles(view) {
    const base = { moves: 0, brood: 0, defend: 1, greedyTarget: 1, rockets: 0, plane: false, guard: true }
    const cost = rules().broodCost
    const ready = view.self.board.map((bf, c) => ((bf.armyQty || 0) >= cost && !bf.isAttacking ? c : null)).filter(c => c != null)
    return [
        { ...base, name: 'guard', aggression: 0.5, minEdge: 2 },
        { ...base, name: 'guard-all', aggression: 1 },
        ...(ready.length ? [{ ...base, name: 'safe-brood', aggression: 0.5, minEdge: 2, broodCols: ready, broodDeep: true, safeBrood: true, noAttackCols: ready }] : []),
    ]
}

export function generateCandidates(view, rng, k, odds, profile = null, { threatAware = false, coverBroods = true, gapClose = true } = {}) {
    const player = { board: view.self.board, homefront: view.self.homefront, yield: view.self.yield }
    const threat = threatAware ? legalThreats(view.opp.board) : null
    const threatBase = threatAware ? legalThreats(view.opp.board, { movers: false }) : null
    const aware = (st) => (threat ? { ...st, threat, threatBase, ...(coverBroods ? {} : { coverBroods: false }), ...(gapClose ? {} : { gapClose: false }) } : st)
    const constrain = (st) => aware(profile?.mutate ? profile.mutate({ ...st }, view, rng) : st)
    const hunts = PROFILES['brood-hunter'].styles(view, rng)
    const styles = [...(profile?.styles?.(view, rng) ?? []), HOLD, ALL_IN, ...(threat ? guardStyles(view) : []), ...broodStyles(view), ...stackStyles(view), ...hunts].map(constrain)
    const out = []
    const seen = new Set()
    const tryStyle = (style) => {
        const plan = buildPlan(view, rng, odds, style)
        const sig = signature(plan)
        if (seen.has(sig)) return
        if (!validateOrders(player, plan).ok) return
        seen.add(sig)
        out.push(plan)
    }
    for (const st of styles) { if (out.length < k) tryStyle(st) }
    for (let i = 0; out.length < k && i < k * 4; i++) {
        const st = randomStyle(rng, i)
        // Threat-aware, some random styles guard, some brood only safely, and
        // a few leave their broods uncovered (so the search can still weigh it).
        if (threat) { st.guard = rng() < 0.4; st.safeBrood = rng() < 0.5; if (coverBroods) st.coverBroods = rng() < 0.85 }
        tryStyle(constrain(st))
    }
    return out
}

export { corridorCells, basesOf }

// What a stack in each corridor could hit NEXT round: for every corridor and
// every winning margin 1..5, the best crater aimable from it (aimScore — known
// structures it would finish count most). The evaluation reads it off the
// post-round boards, so building force where it can reach something is worth
// something before it strikes.
export function threatTable(view, odds) {
    return view.self.board.map((_, col) => {
        const row = [0]
        for (let m = 1; m <= 5; m++) {
            let best = 0
            for (const s of corridorCells(col)) best = Math.max(best, aimScore(view, col, odds, s, m))
            row.push(best)
        }
        return row
    })
}
